import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Pager } from "../components/Pager";
import { Card, ErrorBanner, Pill, fmt } from "../components/ui";
import { api, type Contact } from "../lib/api";

export function Contacts() {
  const [items, setItems] = useState<Contact[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [sort, setSort] = useState("id");
  const [desc, setDesc] = useState(false);
  const [q, setQ] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // One page at a time, replacing rather than appending: a table of thirty
  // thousand rows makes the tab unusable long before the data gets
  // interesting.
  const load = (p: number) => {
    setLoading(true);
    api
      .contacts({ q: q || undefined, page: p, per_page: perPage, sort, desc })
      .then((r) => {
        setItems(r.items);
        setTotal(r.total);
        setPage(r.page);
        setPages(r.pages);
        setError(null);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  // Searching or resorting starts again at page one - staying on page 40 of a
  // result that now has three pages is how a list appears empty for no reason.
  useEffect(() => {
    const t = setTimeout(() => load(1), 300);
    return () => clearTimeout(t);
  }, [q, perPage, sort, desc]);

  const by = (key: string) => () => {
    if (sort === key) setDesc((d) => !d);
    else {
      setSort(key);
      setDesc(key !== "name");
    }
  };
  const caret = (key: string) => (sort === key ? (desc ? " ↓" : " ↑") : "");

  return (
    <>
      <ErrorBanner error={error} />
      <div className="row w">
        <h2 style={{ fontSize: 19 }}>Contacts</h2>
        <div className="sp" />
        <input
          className="f"
          style={{ width: 300 }}
          placeholder="Search a name, email, or phone in any format"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      <Card>
        {items.length === 0 && !loading ? (
          <div className="empty">
            <p style={{ marginBottom: 14 }}>
              {q ? `Nobody matches "${q}".` : "No contacts yet."}
            </p>
            {!q && (
              <Link className="btn p" to="/import">
                Import contacts from a CSV
              </Link>
            )}
          </div>
        ) : (
          <div className="tw">
            <table>
              <thead>
                <tr>
                  <th className="sortable" onClick={by("name")}>Name{caret("name")}</th>
                  <th>Phone</th>
                  <th>State</th>
                  <th>Status</th>
                  <th className="sortable" onClick={by("flights")}>Trips{caret("flights")}</th>
                  <th className="sortable" onClick={by("last_reply")}>
                    Last heard{caret("last_reply")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((c) => (
                  <tr key={c.id}>
                    <td style={{ fontWeight: 600 }}>
                      <Link to={`/contacts/${c.id}`}>{fmt.name(c)}</Link>
                    </td>
                    <td className="mono dim">{c.phone_e164}</td>
                    <td>{c.state ?? "—"}</td>
                    <td>
                      <Pill tone={c.consent_status === "opted_in" ? "ok" : c.consent_status === "opted_out" ? "ba" : "nu"}>
                        {c.consent_status === "opted_in" ? "subscribed" : c.consent_status.replace("_", " ")}
                      </Pill>
                    </td>
                    <td className="mono">{c.flight_count}</td>
                    <td className={c.last_reply_at ? "" : "dim"}>{fmt.when(c.last_reply_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Pager
          page={page}
          pages={pages}
          total={total}
          perPage={perPage}
          busy={loading}
          onPage={load}
          onPerPage={setPerPage}
        />
      </Card>
    </>
  );
}
